import React, { useState, useEffect } from 'react';
import {
  getConsentPreferences,
  saveConsentPreferences,
  acceptAllCookies,
  rejectNonEssentialCookies,
  loadConsentedScripts,
  clearConsent,
} from '../../utils/cookieConsent';

export default function CookieSettingsModal({ isOpen, onClose }) {
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    if (isOpen) {
      const existingConsent = getConsentPreferences();
      if (existingConsent) {
        setPreferences({
          necessary: true,
          functional: existingConsent.functional,
          analytics: existingConsent.analytics,
          marketing: existingConsent.marketing,
        });
      }
    }
  }, [isOpen]);

  const handleSavePreferences = () => {
    saveConsentPreferences(preferences);
    loadConsentedScripts();
    onClose();
  };

  const handleAcceptAll = () => {
    acceptAllCookies();
    loadConsentedScripts();
    onClose();
  };

  const handleRejectNonEssential = () => {
    rejectNonEssentialCookies();
    onClose();
  };

  const handleClearConsent = () => {
    if (window.confirm('Are you sure you want to clear all cookie preferences? You will be asked for consent again.')) {
      clearConsent();
      window.location.reload();
    }
  };

  const toggleCategory = (category) => {
    if (category === 'necessary') return;
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Cookie Settings</h2>
                <p className="text-indigo-100 text-sm mt-1">
                  Manage your cookie preferences
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-indigo-100 focus:outline-none transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            <div className="mb-6">
              <p className="text-gray-600 leading-relaxed">
                We use cookies to provide essential features, remember your preferences, and improve your experience. 
                You can customize which types of cookies you accept below. For more information, see our{' '}
                <a 
                  href="/legal/cookies" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-700 underline font-medium"
                >
                  Cookie Policy
                </a>.
              </p>
            </div>

            <div className="space-y-4">
              {/* Strictly Necessary */}
              <div className="bg-gray-50 rounded-lg p-5 border-2 border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <svg className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <h3 className="font-bold text-gray-900 text-lg">Strictly Necessary Cookies</h3>
                      <span className="text-xs bg-gray-300 text-gray-700 px-2.5 py-1 rounded-full font-semibold">
                        Always Active
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Essential cookies required for the platform to work. These include authentication, 
                      security, session management, and core functionality. Cannot be disabled.
                    </p>
                    <div className="mt-3 text-xs text-gray-500">
                      <strong>Examples:</strong> auth0, auth0_compat, XSRF-TOKEN, _stripe_mid (payments)
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <div className="w-14 h-7 bg-indigo-600 rounded-full flex items-center justify-end px-1 shadow-inner">
                      <div className="w-5 h-5 bg-white rounded-full shadow"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Functional */}
              <div className="bg-white rounded-lg p-5 border-2 border-gray-200 hover:border-indigo-300 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <svg className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <h3 className="font-bold text-gray-900 text-lg">Functional Cookies</h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Enable enhanced features like remembering your search preferences, saved campaigns, 
                      display settings, and personalized UI customizations for a better experience.
                    </p>
                    <div className="mt-3 text-xs text-gray-500">
                      <strong>Examples:</strong> eq_user_preferences, eq_search_history, eq_campaign_view
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <button
                      onClick={() => toggleCategory('functional')}
                      className={`w-14 h-7 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-inner ${
                        preferences.functional ? 'bg-indigo-600' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        preferences.functional ? 'transform translate-x-8' : 'transform translate-x-1'
                      }`}></div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Analytics */}
              <div className="bg-white rounded-lg p-5 border-2 border-gray-200 hover:border-indigo-300 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <svg className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <h3 className="font-bold text-gray-900 text-lg">Analytics Cookies</h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Help us understand how you use the platform through anonymous usage statistics. 
                      This data helps us identify issues, improve features, and enhance user experience.
                    </p>
                    <div className="mt-3 text-xs text-gray-500">
                      <strong>Examples:</strong> eq_analytics_id (Note: We don't currently use Google Analytics or third-party analytics)
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <button
                      onClick={() => toggleCategory('analytics')}
                      className={`w-14 h-7 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-inner ${
                        preferences.analytics ? 'bg-indigo-600' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        preferences.analytics ? 'transform translate-x-8' : 'transform translate-x-1'
                      }`}></div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Marketing */}
              <div className="bg-white rounded-lg p-5 border-2 border-gray-200 hover:border-indigo-300 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <svg className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                      </svg>
                      <h3 className="font-bold text-gray-900 text-lg">Marketing Cookies</h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      May be set by advertising partners to build a profile and show relevant ads. 
                      We do not currently use marketing cookies, but this option is available for future use.
                    </p>
                    <div className="mt-3 text-xs text-gray-500">
                      <strong>Status:</strong> Not currently in use
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <button
                      onClick={() => toggleCategory('marketing')}
                      className={`w-14 h-7 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-inner ${
                        preferences.marketing ? 'bg-indigo-600' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        preferences.marketing ? 'transform translate-x-8' : 'transform translate-x-1'
                      }`}></div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Clear Consent */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={handleClearConsent}
                className="text-sm text-red-600 hover:text-red-700 font-medium underline"
              >
                Clear all cookie preferences and reset
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleRejectNonEssential}
                className="flex-1 px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                Reject Non-Essential
              </button>
              <button
                onClick={handleSavePreferences}
                className="flex-1 px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors shadow-sm"
              >
                Save My Preferences
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors shadow-sm"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

